import React, { useState, useEffect, useRef } from 'react';
import LoadingView from '../../shared/LoadingView';
import Yup from 'yup';
import API from '../../shared/forms/API';

const urlSchema = Yup.string().url('Ongeldige URL');

const WebhookSection = () => {
  const [webhookUrls, setWebhookUrls] = useState([]);
  const [touched, setTouched] = useState([]);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [status, setStatus] = useState(null);
  const [showSecrets, setShowSecrets] = useState({});
  const [testing, setTesting] = useState({});
  const originalIds = useRef(new Set());

  useEffect(() => {
    loadWebhookUrls();
  }, []);

  const loadWebhookUrls = async () => {
    setLoading(true);
    try {
      const response = await new API().fetch('webhook-urls?limit=100');
      if (response.ok) {
        const data = await response.json();
        const urls = data.results || [];
        originalIds.current = new Set(urls.map(item => item.id));
        if (urls.length === 0) {
          setWebhookUrls([{ id: null, url: '', enabled: true, secret: null }]);
          setTouched([false]);
          setErrors([null]);
        } else {
          setWebhookUrls(urls.map(item => ({ id: item.id, url: item.url, enabled: item.enabled, secret: item.secret })));
          setTouched(urls.map(() => false));
          setErrors(urls.map(() => null));
        }
      } else {
        setStatus({ type: 'danger', text: 'Fout bij laden van webhook URLs.' });
      }
    } catch (err) {
      setStatus({ type: 'danger', text: 'Fout bij laden van webhook URLs.' });
    }
    setLoading(false);
  };

  const validateUrl = (value) => {
    if (!value) return null;
    try {
      urlSchema.validateSync(value);
      return null;
    } catch (err) {
      return err.message;
    }
  };

  const handleUrlChange = (index, value) => {
    const newUrls = [...webhookUrls];
    newUrls[index] = { ...newUrls[index], url: value };
    setWebhookUrls(newUrls);

    if (touched[index]) {
      const newErrors = [...errors];
      newErrors[index] = validateUrl(value);
      setErrors(newErrors);
    }
  };

  const handleEnabledChange = (index) => {
    const newUrls = [...webhookUrls];
    newUrls[index] = { ...newUrls[index], enabled: !newUrls[index].enabled };
    setWebhookUrls(newUrls);
  };

  const handleBlur = (index) => {
    const newTouched = [...touched];
    newTouched[index] = true;
    setTouched(newTouched);

    const newErrors = [...errors];
    newErrors[index] = validateUrl(webhookUrls[index].url);
    setErrors(newErrors);
  };

  const addUrl = () => {
    setWebhookUrls([...webhookUrls, { id: null, url: '', enabled: true, secret: null }]);
    setTouched([...touched, false]);
    setErrors([...errors, null]);
  };

  const removeUrl = (index) => {
    if (webhookUrls.length === 1) {
      setWebhookUrls([{ id: null, url: '', enabled: true, secret: null }]);
      setTouched([false]);
      setErrors([null]);
    } else {
      setWebhookUrls(webhookUrls.filter((_, i) => i !== index));
      setTouched(touched.filter((_, i) => i !== index));
      setErrors(errors.filter((_, i) => i !== index));
    }
  };

  const toggleSecretVisibility = (id) => {
    setShowSecrets(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const regenerateSecret = async (id) => {
    if (!window.confirm('Weet je zeker dat je een nieuw secret wilt genereren? Bestaande integraties moeten worden bijgewerkt.')) {
      return;
    }
    try {
      const response = await new API().fetch(`webhook-urls/${id}/regenerate-secret`, 'POST');
      if (response.ok) {
        const data = await response.json();
        setWebhookUrls(prev => prev.map(item =>
          item.id === id ? { ...item, secret: data.secret } : item
        ));
        setStatus({ type: 'success', text: 'Secret opnieuw gegenereerd.' });
      } else {
        setStatus({ type: 'danger', text: 'Fout bij genereren van nieuw secret.' });
      }
    } catch (err) {
      setStatus({ type: 'danger', text: 'Fout bij genereren van nieuw secret.' });
    }
  };

  const testWebhook = async (id) => {
    setTesting(prev => ({ ...prev, [id]: true }));
    setStatus(null);
    try {
      const response = await new API().fetch(`webhook-urls/${id}/test`, 'POST');
      const data = await response.json();
      if (data.success) {
        setStatus({ type: 'success', text: `Test webhook verzonden (HTTP ${data.status_code}).` });
      } else {
        setStatus({ type: 'danger', text: `Test webhook mislukt: ${data.error}` });
      }
    } catch (err) {
      setStatus({ type: 'danger', text: 'Fout bij verzenden van test webhook.' });
    }
    setTesting(prev => ({ ...prev, [id]: false }));
  };

  const handleSave = async () => {
    const newTouched = webhookUrls.map(() => true);
    setTouched(newTouched);

    const newErrors = webhookUrls.map(item => validateUrl(item.url));
    setErrors(newErrors);

    const urlsToSave = webhookUrls.filter(item => item.url.trim() !== '');
    const hasErrors = urlsToSave.some((item) => {
      const originalIndex = webhookUrls.findIndex(w => w === item);
      return newErrors[originalIndex] !== null;
    });

    if (hasErrors) {
      setStatus({ type: 'danger', text: 'Corrigeer de fouten voordat je opslaat.' });
      return;
    }

    setSaving(true);
    setStatus(null);

    try {
      // Find IDs to delete (were in original set but not in current list)
      const currentIds = new Set(webhookUrls.filter(item => item.id).map(item => item.id));
      const idsToDelete = [...originalIds.current].filter(id => !currentIds.has(id));

      // Delete removed items
      for (const id of idsToDelete) {
        const response = await new API().fetch(`webhook-urls/${id}`, 'DELETE');
        if (!response.ok) {
          throw new Error('Delete failed');
        }
      }

      // Create or update items
      for (const item of urlsToSave) {
        if (item.id) {
          const response = await new API().submit(`webhook-urls/${item.id}`, 'PUT', {
            webhook_url: {
              url: item.url,
              enabled: item.enabled
            }
          });
          if (!response.ok) {
            throw new Error('Update failed');
          }
        } else {
          const response = await new API().submit('webhook-urls', 'POST', {
            webhook_url: {
              url: item.url,
              enabled: item.enabled
            }
          });
          if (!response.ok) {
            throw new Error('Create failed');
          }
        }
      }

      setStatus({ type: 'success', text: 'Wijzigingen opgeslagen.' });
      await loadWebhookUrls();
    } catch (err) {
      setStatus({ type: 'danger', text: 'Fout bij opslaan van wijzigingen.' });
    }

    setSaving(false);
  };

  const hasValidUrls = webhookUrls.some(item => item.url.trim() !== '' && !validateUrl(item.url));
  const hasItemsToDelete = originalIds.current.size > 0 &&
    [...originalIds.current].some(id => !webhookUrls.find(item => item.id === id));
  const canSave = hasValidUrls || hasItemsToDelete;

  if (loading) {
    return (
      <div className="card mb-4">
        <div className="card-body">
          <h4 className="card-title mb-4">Webhooks <span className="badge badge-warning float-right">BETA</span></h4>
          <LoadingView className="h2 my-4" />
        </div>
      </div>
    );
  }

  return (
    <div className="card mb-4">
      <div className="card-body">
        <h4 className="card-title mb-4">Webhooks <span className="badge badge-warning float-right">BETA</span></h4>
        {status && (
          <div className={`alert alert-${status.type}`}>{status.text}</div>
        )}
        <p>
          Voer publiek toegankelijke URLs in om webhook notificaties te ontvangen. Gebruik het vinkje om een webhook tijdelijk uit te schakelen.
          Het secret kan gebruikt worden om de <code>X-Webhook-Signature</code> header te valideren (HMAC-SHA256).
        </p>
        {webhookUrls.map((item, index) => (
          <div key={item.id || `new-${index}`} className="mb-3 pb-3 border-bottom">
            <div className="input-group mb-2">
              <div className="input-group-prepend">
                <div className="input-group-text">
                  <input
                    type="checkbox"
                    checked={item.enabled}
                    onChange={() => handleEnabledChange(index)}
                    disabled={saving}
                    title={item.enabled ? 'Actief' : 'Inactief'}
                  />
                </div>
              </div>
              <input
                type="url"
                className={`form-control ${touched[index] && errors[index] ? 'is-invalid' : ''} ${!item.enabled ? 'text-muted' : ''}`}
                placeholder="https://example.com/webhook"
                value={item.url}
                onChange={(e) => handleUrlChange(index, e.target.value)}
                onBlur={() => handleBlur(index)}
                disabled={saving}
              />
              <div className="input-group-append">
                {item.id && (
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => testWebhook(item.id)}
                    title="Test webhook verzenden"
                    disabled={saving || testing[item.id]}
                  >
                    {testing[item.id] ? <i className="fa fa-spinner fa-spin" /> : <i className="fa fa-paper-plane" />}
                  </button>
                )}
                <button
                  type="button"
                  className="btn btn-outline-danger"
                  onClick={() => removeUrl(index)}
                  title="Verwijderen"
                  disabled={saving}
                >
                  <i className="fa fa-trash" />
                </button>
              </div>
            </div>
            {touched[index] && errors[index] && (
              <div className="invalid-feedback d-block">{errors[index]}</div>
            )}
            {item.id && item.secret && (
              <div className="d-flex align-items-center mt-2">
                <small className="text-muted mr-2">Secret:</small>
                <code className="mr-2">
                  {showSecrets[item.id] ? item.secret : '••••••••••••••••'}
                </code>
                <button
                  type="button"
                  className="btn btn-sm btn-link p-0 mr-2"
                  onClick={() => toggleSecretVisibility(item.id)}
                  title={showSecrets[item.id] ? 'Verbergen' : 'Tonen'}
                >
                  <i className={`fa fa-eye${showSecrets[item.id] ? '-slash' : ''}`} />
                </button>
                <button
                  type="button"
                  className="btn btn-sm btn-link p-0"
                  onClick={() => regenerateSecret(item.id)}
                  title="Nieuw secret genereren"
                  disabled={saving}
                >
                  <i className="fa fa-refresh" />
                </button>
              </div>
            )}
            {!item.id && (
              <small className="text-muted">Secret wordt gegenereerd na opslaan.</small>
            )}
          </div>
        ))}
        <button
          type="button"
          className="btn btn-outline-secondary mb-3"
          onClick={addUrl}
          disabled={saving}
        >
          <i className="fa fa-plus mr-1" /> URL toevoegen
        </button>
        <div>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saving || !canSave}
          >
            {saving ? <i className="fa fa-spinner fa-spin mr-1" /> : null}
            Opslaan
          </button>
        </div>
      </div>
    </div>
  );
};

export default WebhookSection;
